import { useMutation } from "@tanstack/react-query";

import { useAffirmCheckout } from "@/hooks/useAffirmCheckout";
import { useKlarnaCheckout } from "@/hooks/useKlarnaCheckout";
import {
  addRedirectPaymentMethod,
  BillingAddressPayload,
  PaymentBehavior,
} from "@/networking/payments.api";
import { isApiError } from "@/lib/api-helpers";
import { sanitizeErrorMessage } from "@/lib/error-sanitizer";
import { getBillingAddressPayload, getPaymentBehavior } from "@/lib/utils";
import { CartResponse } from "@/networking/carts.api";

interface ShippingInfo {
  first_name: string;
  last_name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  country_code: string;
  email: string;
}

interface AdditionalData {
  cartData: CartResponse | null;
  affirmPublicKey?: string;
  shippingInfo?: ShippingInfo;
}

interface HandlePaymentMethodParams {
  paymentType: string;
  onSuccess: () => Promise<void>;
  setPaymentError: (error: string | undefined) => void;
  additionalData?: AdditionalData;
  customer_details?: Record<string, string>;
  onCustomerDetailsValidationErrors?: (
    errors: Record<string, string[]>,
  ) => void;
  collectBillingAddress?: boolean;
  useShippingAddressForBilling?: boolean;
}

export function usePaymentMethodHandler(cartToken: string) {
  const { handleTokenizeAffirmCheckout, affirmModalLoading } =
    useAffirmCheckout();
  const { handleKlarnaCheckout, klarnaModalLoading } = useKlarnaCheckout();

  const redirectPaymentMutation = useMutation({
    mutationFn: async ({
      paymentType,
      onSuccess,
      payment_account,
      customer_details,
      billing_address,
    }: {
      paymentType: string;
      onSuccess: () => Promise<void>;
      payment_account?: string;
      customer_details?: Record<string, string>;
      billing_address?: BillingAddressPayload;
    }) => {
      await addRedirectPaymentMethod({
        cartToken,
        paymentType,
        payment_account,
        customer_details,
        billing_address,
      });
      await onSuccess();
    },
    onError: (error: unknown) => {
      console.error("Redirect payment error:", error);
    },
  });

  const handlePaymentMethod = async ({
    paymentType,
    onSuccess,
    setPaymentError,
    additionalData,
    customer_details,
    onCustomerDetailsValidationErrors,
    collectBillingAddress,
    useShippingAddressForBilling,
  }: HandlePaymentMethodParams) => {
    const behavior = getPaymentBehavior(paymentType);

    let billingAddressPayload: BillingAddressPayload | undefined;

    if (additionalData?.cartData?.cart) {
      const cart = additionalData.cartData.cart;
      const addressSource = collectBillingAddress
        ? useShippingAddressForBilling
          ? cart.ship_to
          : cart.bill_to
        : cart.ship_to;

      if (addressSource) {
        const fullPayload = getBillingAddressPayload(addressSource);
        billingAddressPayload = {
          name: fullPayload.name,
          address1: fullPayload.address1,
          address2: fullPayload.address2,
          city: fullPayload.city,
          state: fullPayload.state,
          postal_code: fullPayload.postal_code,
          country_code: fullPayload.country_code,
        };
      }
    }

    try {
      switch (behavior) {
        case PaymentBehavior.MODAL_BASED:
          if (paymentType === "affirm") {
            // Check if we have required data for Affirm
            if (!additionalData?.shippingInfo) {
              setPaymentError(
                "Shipping information is required for Affirm checkout",
              );
              return;
            }

            await handleTokenizeAffirmCheckout({
              cartToken,
              cartData: additionalData.cartData ?? null,
              affirmPublicKey: additionalData.affirmPublicKey,
              onSuccess,
              setPaymentError,
              shippingInfo: additionalData.shippingInfo,
              billingAddress: billingAddressPayload,
            });
          } else if (paymentType === "klarna") {
            await handleKlarnaCheckout({
              cartToken,
              onSuccess,
              setPaymentError,
            });
          }
          break;

        case PaymentBehavior.REDIRECT_BASED:
          try {
            await redirectPaymentMutation.mutateAsync({
              paymentType,
              onSuccess,
              payment_account:
                additionalData?.cartData?.cart?.payment_account ?? undefined,
              customer_details,
              billing_address: billingAddressPayload,
            });
          } catch (error) {
            if (isApiError(error)) {
              if (
                error.data &&
                typeof error.data === "object" &&
                "errors" in error.data
              ) {
                const errorsData = error.data.errors;

                // handle customer_details errors (object format)
                if (
                  errorsData &&
                  typeof errorsData === "object" &&
                  !Array.isArray(errorsData) &&
                  "customer_details" in errorsData
                ) {
                  const errors = errorsData as {
                    customer_details?: Record<string, string[]>;
                  };
                  if (
                    errors.customer_details &&
                    typeof errors.customer_details === "object"
                  ) {
                    const customerDetailsErrors: Record<string, string[]> = {};
                    Object.keys(errors.customer_details).forEach((key) => {
                      const fieldErrors = errors.customer_details?.[key];
                      if (Array.isArray(fieldErrors)) {
                        customerDetailsErrors[key] = fieldErrors;
                      }
                    });
                    if (Object.keys(customerDetailsErrors).length > 0) {
                      onCustomerDetailsValidationErrors?.(
                        customerDetailsErrors,
                      );
                    }
                  }
                }

                // handle errors array with JSON strings (e.g. bill_to validation)
                if (Array.isArray(errorsData)) {
                  const parsedErrors: string[] = [];
                  errorsData.forEach((errStr) => {
                    if (typeof errStr === "string") {
                      try {
                        const parsed = JSON.parse(errStr);
                        // extract field errors like {"bill_to":{"address2":["is required"]}}
                        Object.entries(parsed).forEach(([_section, fields]) => {
                          if (fields && typeof fields === "object") {
                            Object.entries(
                              fields as Record<string, string[]>,
                            ).forEach(([field, messages]) => {
                              if (Array.isArray(messages)) {
                                const fieldName = field
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (c) => c.toUpperCase());
                                parsedErrors.push(
                                  `${fieldName} ${messages.join(", ")}`,
                                );
                              }
                            });
                          }
                        });
                      } catch {
                        // not JSON, use as-is
                        parsedErrors.push(errStr);
                      }
                    }
                  });

                  if (parsedErrors.length > 0) {
                    setPaymentError(parsedErrors.join(". "));
                    return;
                  }
                }
              }

              const errorMessage =
                error &&
                typeof error === "object" &&
                "data" in error &&
                error.data &&
                typeof error.data === "object" &&
                "error_message" in error.data
                  ? (error.data as { error_message?: string }).error_message
                  : undefined;

              setPaymentError(
                sanitizeErrorMessage(
                  errorMessage,
                  "Payment method failed. Please check your information and try again.",
                ),
              );
            } else {
              setPaymentError(
                sanitizeErrorMessage(
                  error instanceof Error ? error.message : undefined,
                  "Payment method failed. Please try again.",
                ),
              );
            }
          }
          break;

        case PaymentBehavior.DIRECT_CHECKOUT:
          await onSuccess();
          break;

        case PaymentBehavior.CREDIT_CARD:
          throw new Error("Credit card should be handled separately");

        case PaymentBehavior.CUSTOM_COMPONENT:
          break;

        default:
          setPaymentError(
            "This payment method is not supported. Please choose a different one.",
          );
          break;
      }
    } catch (error) {
      console.error("Payment method error:", error);
      setPaymentError(
        sanitizeErrorMessage(
          error instanceof Error ? error.message : undefined,
          "Payment processing failed. Please try again.",
        ),
      );
    }
  };

  const isPaymentLoading = (paymentType: string): boolean => {
    const behavior = getPaymentBehavior(paymentType);

    switch (behavior) {
      case PaymentBehavior.MODAL_BASED:
        if (paymentType === "affirm") return affirmModalLoading;
        if (paymentType === "klarna") return klarnaModalLoading;
        return false;
      case PaymentBehavior.REDIRECT_BASED:
        return redirectPaymentMutation.isPending;
      default:
        return false;
    }
  };

  return {
    handlePaymentMethod,
    isPaymentLoading,
    redirectPaymentMutation,
  };
}
